import React from 'react';
import FormikTextField from 'components/inputs/FormikTextField';
import InputAdornment from '@mui/material/InputAdornment';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import IconButton from '@mui/material/IconButton';
import {useTranslation} from 'react-i18next';
import {useModal} from 'common/hooks/useModal';
import FileManagerDialog from 'features/layout/DialogRoutes/dialogs/FileManagerDialog';
import {readOnlyContext} from 'common/context/ReadOnlyContext';
import {loginSelectors} from 'features/login/redux';
import {useSelector} from 'react-redux';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import {useRegisterLabel} from 'common/context/FormContext';

type Props = {
   name: string;
   placeholder?: string;
   label?: string;
   disabled?: boolean;
   directoriesOnly?: boolean;
   filesOfType?: string[];
   autoFocus?: boolean;
   promptOnChange?: string;
};

interface BrowseFileInputProps extends Props {
   value: string;
   setValue: (val: string) => void;
}

const BrowseFileInput = React.memo(
   ({
      name,
      placeholder,
      label,
      disabled = false,
      directoriesOnly,
      filesOfType,
      autoFocus,
      value,
      setValue,
      promptOnChange,
   }: BrowseFileInputProps) => {
      const {t} = useTranslation();
      const {open, handleOpen, handleClose} = useModal();
      const {resetField} = useFormContext();
      const readOnly = React.useContext(readOnlyContext);

      const [confirmed, setConfirmed] = React.useState(!promptOnChange);
      const [confirmOpen, setConfirmOpen] = React.useState(false);
      const triggerConfirmation = () => {
         // setConfirmed(false);
         !confirmed && setConfirmOpen(true);
      };
      const onOk = () => {
         setConfirmed(true);
         setConfirmOpen(false);
      };
      const onCancel = () => {
         resetField(name, {
            keepDirty: true,
            keepError: true,
            keepTouched: true,
         });
         setConfirmOpen(false);
      };

      const isReviewer = useSelector(loginSelectors.isReviewer);
      return (
         <>
            <FormikTextField
               autoFocus={autoFocus}
               name={name}
               label={label || placeholder}
               disabled={disabled || readOnly}
               onChange={confirmed ? undefined : triggerConfirmation}
               InputProps={{
                  endAdornment: !isReviewer && (
                     <InputAdornment position={'end'}>
                        <IconButton
                           size={'small'}
                           title={t('Browse')}
                           onClick={confirmed ? handleOpen : triggerConfirmation}
                           disabled={disabled || readOnly}
                        >
                           <MoreHorizIcon />
                        </IconButton>
                     </InputAdornment>
                  ),
               }}
            />
            <FileManagerDialog
               open={open}
               initialPath={value}
               title={t('File location')}
               directoriesOnly={directoriesOnly}
               filesOfType={filesOfType}
               onConfirm={(path) => {
                  handleClose();
                  setValue(path);
               }}
               onClose={handleClose}
            />
            {confirmOpen && (
               <ConfirmationDialog
                  open={confirmOpen}
                  title={t('Warning')}
                  content={promptOnChange}
                  onCancel={onCancel}
                  onOk={onOk}
               />
            )}
         </>
      );
   }
);

const HookFormBrowseFileInput = (props: Props) => {
   const {
      field: {value},
   } = useController({name: props.name});
   useRegisterLabel(props.name, props.placeholder || props.label);
   const {setValue: _setValue} = useFormContext();

   const setValue = React.useCallback(
      (val: number | string) => _setValue(props.name, val, SetValueOptions),
      [props.name, _setValue]
   );

   return <BrowseFileInput {...props} value={value} setValue={setValue} />;
};

export default HookFormBrowseFileInput;
